import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { LoginModel } from 'src/app/models/loginModel';
import { RegisterModel } from 'src/app/models/registerModel';
import { SessionService } from 'src/app/services/session-service';
import { UserService } from 'src/app/services/user-service';
import { PendingFavouriteService } from 'src/app/services/pending-favourite-service';
import { getOAuthApiOrigin } from 'src/environments/oauth-api-origin';

@Component({
  selector: 'pla-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.css'],
})
export class LoginPageComponent implements OnInit {
  public loginModel: LoginModel = new LoginModel();
  public registerModel: RegisterModel = new RegisterModel();
  public loading: boolean = false;

  constructor(
    private sessionService: SessionService,
    private snackbar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private pendingFavourite: PendingFavouriteService
  ) {
    if (sessionService.isLoggedIn()) {
      this.router.navigate([this.sessionService.getSuccessRoute()]);
    }
  }

  ngOnInit(): void {
    this.handleSocialCallback();
    this.initializeGoogleSignIn();
    this.initializeFacebookSDK();
  }

  private handleSocialCallback(): void {
    this.route.queryParamMap.subscribe((params) => {
      const social = params.get('social');
      const error = params.get('error');
      if (social !== 'apple') return;

      if (error) {
        this.snackbar.open(`Apple sign-in failed: ${error}`, 'Close', {
          duration: 9000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['social-auth-error-snackbar'],
        });
        return;
      }

      this.loading = true;
      this.sessionService.RefreshToken().subscribe(
        () => {
          this.replayPendingFavourite();
          const route = this.sessionService.getSuccessRoute();
          this.router.navigate([route]);
        },
        () => {
          this.loading = false;
          this.snackbar.open('Apple sign-in could not be completed. Please try again.', 'Close', {
            duration: 9000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['social-auth-error-snackbar'],
          });
        }
      );
    });
  }

  // Replays a favourite/notify intent that was persisted before a social
  // redirect (e.g. Apple), so it is saved once the user returns signed in.
  private replayPendingFavourite(): void {
    const pending = this.pendingFavourite.get();
    if (!pending) return;
    this.pendingFavourite.clear();
    if (pending.action === 'notify') {
      this.userService.notifyRegistrationString(pending.registration);
    } else {
      this.userService.favouriteRegistrationString(pending.registration);
    }
  }

  public login(): void {
    this.makeLoginRequest(this.loginModel);
  }

  public signUp(): void {
    this.router.navigate(['/register']);
  }

  public forgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  private initializeGoogleSignIn(): void {
    const checkGoogleLoaded = setInterval(() => {
      if (typeof (window as any).google !== 'undefined') {
        clearInterval(checkGoogleLoaded);
        (window as any).google.accounts.id.initialize({
          client_id: '505978889074-sj1une8r7955ofq2uasjq11g0eia8p6r.apps.googleusercontent.com',
          callback: (response: any) => this.handleGoogleCredentialResponse(response),
          auto_select: false,
        });
      }
    }, 100);

    setTimeout(() => { clearInterval(checkGoogleLoaded); }, 10000);
  }

  public googleLogin(): void {
    if (typeof (window as any).google === 'undefined') {
      this.snackbar.open('Google sign-in is not available. Please refresh and try again.', 'Close', { duration: 5000 });
      return;
    }
    (window as any).google.accounts.id.prompt();
  }

  private handleGoogleCredentialResponse(response: any): void {
    if (!response.credential) {
      this.snackbar.open('Google sign-in was cancelled or failed', 'Close', { duration: 5000 });
      return;
    }

    this.loading = true;
    this.sessionService.handleGoogleLogin(
      response.credential,
      'login-page',
      (success: boolean, message: string) => {
        if (!success) {
          this.snackbar.open(message, 'Close', { duration: 5000 });
          this.loading = false;
        } else {
          var route = this.sessionService.getSuccessRoute();
          setTimeout(() => {
            this.router.navigate([route]);
          }, 500);
        }
      }
    );
  }

  private initializeFacebookSDK(): void {
    // Wait for Facebook SDK to load
    const checkFacebookLoaded = setInterval(() => {
      if (typeof (window as any).FB !== 'undefined') {
        clearInterval(checkFacebookLoaded);
        this.configureFacebookSDK();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkFacebookLoaded);
    }, 10000);
  }

  private configureFacebookSDK(): void {
    (window as any).FB.init({
      appId: '1163605102162389',
      cookie: true,
      xfbml: true,
      version: 'v21.0'
    });
  }

  public facebookLogin(): void {
    if (typeof (window as any).FB === 'undefined') {
      this.snackbar.open('Facebook SDK not loaded. Please refresh and try again.', 'Close', { duration: 5000 });
      return;
    }

    (window as any).FB.login((response: any) => {
      if (response.authResponse) {
        this.handleFacebookAuthResponse(response.authResponse);
      } else {
        console.log('User cancelled Facebook login or did not fully authorize.');
      }
    }, { scope: 'public_profile,email' });
  }

  public appleLogin(): void {
    const authBaseUrl = getOAuthApiOrigin();
    const returnUrl = encodeURIComponent(`${window.location.origin}${window.location.pathname}`);
    window.location.href = `${authBaseUrl}/auth/apple?signupReason=login-page&returnUrl=${returnUrl}`;
  }

  private handleFacebookAuthResponse(authResponse: any): void {
    const accessToken = authResponse.accessToken;
    
    if (!accessToken) {
      this.snackbar.open('Facebook sign-in failed. Please try again.', 'Close', { duration: 5000 });
      return;
    }

    this.loading = true;
    this.sessionService.handleFacebookLogin(
      accessToken,
      'login-page',
      (success: boolean, message: string) => {
        if (!success) {
          this.snackbar.open(message, 'Close', { duration: 5000 });
          this.loading = false;
        } else {
          var route = this.sessionService.getSuccessRoute();
          setTimeout(() => {
            this.router.navigate([route]);
          }, 500);
        }
      }
    );
  }

  private makeLoginRequest(loginDetails: LoginModel): void {
    this.loading = true;
    this.sessionService.login(
      loginDetails,
      (success: boolean, message: string) => {
        if (!success) {
          this.snackbar.open(message, 'Close', {
            duration: 5000,
            panelClass: ['auth-error-snackbar'],
          });
          this.loading = false;
        } else {
          var route = this.sessionService.getSuccessRoute();
          setTimeout(() => {
            this.router.navigate([route]);
          }, 500);
        }
      }
    );
  }
}
